<template>
  <div>
    <b-modal
      size="xlt"
      v-if="!hasItem && openClicked"
      ref="myModal"
      id="add-model"
      title="Add a configurable material"
      centered
      no-close-on-backdrop
      hide-footer
      body-class="find-modal-body"
    >
      <div class="px-2 pt-0 pb-2">
        <label class="sr-only" for="config-material-search">Search by PCN or description</label>
        <b-form-input
          id="config-material-search"
          v-model.trim="materialSearchQuery"
          type="search"
          placeholder="Search by PCN or description…"
          autocomplete="off"
          autofocus
        />
      </div>
      <b-row class="mx-2 mb-3 configurable-material-grid">
        <div
          v-for="mat in filteredConfigurableMaterials"
          :key="mat.PcnCatalogNumber"
          class="col-md-3 mb-3"
        >
          <div
            class="config-material-card"
            role="button"
            tabindex="0"
            :title="'Click to add ' + (mat.PcnCatalogNumber || '')"
            @click="handleAddItemToOrder(mat, 1)"
            @keydown.enter.prevent="handleAddItemToOrder(mat, 1)"
            @keydown.space.prevent="handleAddItemToOrder(mat, 1)"
          >
            <div class="config-material-card__pcn">
              <span
                v-for="(seg, i) in highlightParts(mat.PcnCatalogNumber, materialSearchQuery)"
                :key="mat.PcnCatalogNumber + '-pcn-' + i"
              >
                <mark v-if="seg.mark" class="config-material-highlight">{{ seg.text }}</mark>
                <span v-else>{{ seg.text }}</span>
              </span>
            </div>
            <div class="config-material-card__desc">
              <span
                v-for="(seg, i) in highlightParts(mat.SapModelDescription, materialSearchQuery)"
                :key="mat.PcnCatalogNumber + '-desc-' + i"
              >
                <mark v-if="seg.mark" class="config-material-highlight">{{ seg.text }}</mark>
                <span v-else>{{ seg.text }}</span>
              </span>
            </div>
            <div v-if="materialImageSrc(mat)" class="config-material-card__thumb">
              <b-img
                :src="materialImageSrc(mat)"
                fluid
                thumbnail
                class="config-material-card__img"
                :alt="mat.SapModelDescription || mat.PcnCatalogNumber || 'Model'"
              />
            </div>
          </div>
        </div>
      </b-row>
      <p
        v-if="materialSearchQuery && filteredConfigurableMaterials.length === 0"
        class="text-muted text-center small px-3 pb-3 mb-0"
      >
        No materials match “{{ materialSearchQuery }}”.
      </p>
    </b-modal>
    <configurable-material-modal ref="matmodal" :cartItem="selectedItem" :materialNumber="selectedItem.pcnCatalogNumber" :userValues="selectedItem.characteristics" v-if="hasItem && openClicked && selectedItem.configurable"></configurable-material-modal>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Watch } from 'vue-property-decorator'

import ModelEditor from '@/components/editor/modals/model-editor.vue'
import StatusBadge from '@/components/editor/status-badge.vue'
import EditorEditor from '@/components/editor/utility/editor-editor.vue'
import LoadingText from '@/components/utility/loading-text.vue'
import ConfigurableMaterialModal from '../modals/configurable-material-modal.vue'

import { namespace } from 'vuex-class'

import MxSite from '@/mixins/site'
import OrderProductTabs from './order-product-tabs.vue'
import OrderProductTopology from './order-product-topology.vue'
import { CartItem } from '@/store/cart/types'
const nsCart = namespace('cart')
const nsConfigurableMaterial = namespace('configurable-material')
const nsOrder = namespace('orders')
const nsInquiry = namespace('inquiries')

@Component({
  components: {
    'loading-text': LoadingText,
    'editor-editor': EditorEditor,
    'status-badge': StatusBadge,
    'model-editor': ModelEditor,
    'order-product-topology': OrderProductTopology,
    'order-product-tabs': OrderProductTabs,
    'configurable-material-modal': ConfigurableMaterialModal,
  },
})
export default class extends MxSite {
  @Prop()
  private item!: CartItem

  @Prop()
  private findingModel!: boolean

  @nsCart.Action('saveCartItem')
  private saveCartItem: any

  @nsConfigurableMaterial.Action('fetchConfigurableMaterials')
  private fetchConfigurableMaterials: any

  @nsConfigurableMaterial.Getter('configurableMaterials')
  public configurableMaterials: any

  @nsInquiry.Getter('currentCustomer')
  public currentInquiryCustomer!: string

  @nsOrder.Getter('currentCustomer')
  public currentOrderCustomer!: string

  public get currentCustomer() {
    if (this.$route.name === 'new-order') {
      return this.currentOrderCustomer
    }
    return this.currentInquiryCustomer
  }

  public materialSearchQuery = ''

  public get filteredConfigurableMaterials(): any[] {
    const list = this.configurableMaterials || []
    const q = (this.materialSearchQuery || '').trim().toLowerCase()
    if (!q) {
      return list
    }
    return list.filter((mat: any) => {
      const pcn = (mat.PcnCatalogNumber || '').toLowerCase()
      const desc = (mat.SapModelDescription || '').toLowerCase()
      return pcn.includes(q) || desc.includes(q)
    })
  }

  /**
   * Split label into plain / highlighted spans (case-insensitive), preserving original casing in the output.
   */
  public highlightParts(
    text: string | null | undefined,
    query: string,
  ): Array<{ text: string; mark: boolean }> {
    const t = text ?? ''
    const q = (query || '').trim()
    if (!q) {
      return [{ text: t, mark: false }]
    }
    const lowerT = t.toLowerCase()
    const lowerQ = q.toLowerCase()
    const parts: Array<{ text: string; mark: boolean }> = []
    let start = 0
    let idx = lowerT.indexOf(lowerQ, start)
    while (idx >= 0) {
      if (idx > start) {
        parts.push({ text: t.slice(start, idx), mark: false })
      }
      parts.push({ text: t.slice(idx, idx + q.length), mark: true })
      start = idx + q.length
      idx = lowerT.indexOf(lowerQ, start)
    }
    if (start < t.length) {
      parts.push({ text: t.slice(start), mark: false })
    }
    return parts.length > 0 ? parts : [{ text: t, mark: false }]
  }

  /** Thumbnail URL when API provides Image.ImagePath or Image.Path; same pattern as cart line thumbnails. */
  public materialImageSrc(mat: any): string {
    const path = mat?.Image?.ImagePath || mat?.Image?.Path || ''
    if (!path || typeof path !== 'string') {
      return ''
    }
    const sep = path.includes('?') ? '&' : '?'
    return `${path}${sep}width=96&height=96`
  }

  public selectedItem: any = null
  public openClicked = false

  public mounted() {
    if (this.item.pcnCatalogNumber) {
      this.selectedItem = this.item
    } else {
      this.fetchConfigurableMaterials()
    }
  }

  @Watch('item', { deep: true })
  private itemChanged() {
    this.selectedItem = this.item
  }

  public open() {
    if (!this.currentCustomer) {
      this.toastError('Please select a customer to add a configurable material')
      return
    }
    this.openClicked = true
    this.materialSearchQuery = ''
    this.$nextTick(() => {
      if (this.hasItem) {
        if (this.selectedItem.configurable) {
          const myModal: any = this.$refs.matmodal
          myModal.show()
        }
      } else {
        const myModal: any = this.$refs.myModal
        myModal.show()
      }
    })
  }

  public async handleAddItemToOrder(item: any, quantity: number) {
    item.TotalList = item.TotalList ?? 0
    const myModal: any = this.$refs.myModal
    const cartItem: CartItem = {
      pcnCatalogNumber: item.PcnCatalogNumber || '',
      productHierarchy: item.ProductHierarchy || '',
      fingoodTpiMfgMod: item.FingoodTpiMfgMod || '',
      description:
        item.SapModelDescription || item.Details.Description?.toString() || '',
      originalPrice: parseFloat(item.TotalList.toString()) || 0,
      price: 0,
      totalPrice: 0 * parseInt(quantity.toString()),
      quantity: parseInt(quantity.toString()),
      imgThumbnail: item.Image?.ImagePath || '',
      inventory: item.Available,
      modelDetails: item.Details,
      leadTime: item.LeadTime || 0,
      configurable: item.Configurable,
    }
    // check for valid price
    this.saveCartItem({ cartItem })
      .then((item) => {
        this.selectedItem = item
        myModal.hide()
      })
      .catch(() => {
        this.toastError('Fail to add item to Order')
      })
  }

  public get hasItem(): boolean {
    return this.selectedItem != null && this.selectedItem.pcnCatalogNumber.length > 0
  }
}
</script>
<style scoped>
.configurable-material-grid {
  max-height: 55vh;
  overflow-y: auto;
}

.config-material-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 140px;
  padding: 0.75rem;
  border: 2px solid #dee2e6;
  border-radius: 0.25rem;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.config-material-card:hover {
  outline: none;
  border-color: #007bff;
  background-color: #f0f7ff;
  box-shadow: 0 0.25rem 0.75rem rgba(0, 123, 255, 0.2);
}

.config-material-card:focus {
  outline: none;
  border-color: #007bff;
  background-color: #e7f1ff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.35);
}

.config-material-card:active {
  background-color: #d9e9ff;
}

.config-material-card__pcn {
  font-weight: 600;
  word-break: break-word;
}

.config-material-card__desc {
  word-break: break-word;
  color: #6c757d;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.config-material-card__thumb {
  margin-top: 0.5rem;
  text-align: center;
  pointer-events: none;
}

.config-material-card__img {
  max-height: 80px;
  max-width: 100%;
  object-fit: contain;
}

.config-material-highlight {
  padding: 0 0.12em;
  margin: 0 -0.02em;
  background-color: #fff3cd;
  color: inherit;
  border-radius: 0.15em;
  font-weight: inherit;
}
</style>
<style>
.find-modal-body {
  min-height: 200px;
}
.filter-dropdown {
  min-width: 600px !important;
  /* height: 200px!important; */
  /* overflow: auto; */
}
.filter-tab {
  overflow: auto;
  height: 350px !important;
}
.modal-dialog {
  max-width: 90% !important;
}

.input-quantity {
  width: 30px !important;
  text-align: center;
}

.mobile-tabs-content {
  height: 50vh;
  overflow: auto;
}

@media (max-width: 767px) {
  .category-browser {
    display: none;
  }
  .filter-dropdown {
    min-width: 100vw !important;
  }
  .mobile-field-dropdown {
    text-wrap: balance !important;
    line-height: initial !important;
    min-width: 40vw !important;
  }
}
</style>
